import { useState, useEffect } from 'react';
import { supabase, getCurrentUser, signIn, signOut, getAllClients, getClientsForAdvisor, getClientForUser, getClientByInviteToken, claimClientInvite, getAdvisorByInviteToken, claimAdvisorInvite, createAdvisorWithPin, getAllAdvisors, sendPasswordResetEmail, getClientCheckslistProgress, getRecentActivityForClients, getKnowledgeBase, createKnowledgeEntry, updateKnowledgeEntry, deleteKnowledgeEntry, getKnowledgeCategories, getTeamMemberProfileByInviteToken, verifyTeamMemberPin, claimTeamMemberInvite, getMyTeamMemberContext, markTeamMemberWelcomeSeen } from './supabase';
import Portal from './Portal';
import ChangePasswordCard from './ChangePasswordCard';
import ChangeEmailCard from './ChangeEmailCard';
import AddClientForm from './AddClientForm';
import AdminDirectory from './AdminDirectory';
import { ActionButton } from './PortalControls';
import { TopBar, Sidebar, SidebarItem, PageHeader, MobileHamburger, useIsMobile, AppShell, useNavState, ImpersonationBanner } from './tokens';
import { Users, Compass, BookOpen, BarChart3, List, LayoutGrid, Activity, Eye, EyeOff, Shield } from 'lucide-react';
import { EMAILS_ENABLED } from './config';

// ── Design tokens ─────────────────────────────────────────────
export const C = {
  bg:"#F6F7FA", bgAlt:"#ECEEF4", surface:"#FFFFFF",
  navy:"#1E3A5F", navyMid:"#2D5282", navyLight:"#5A82B4",
  navyFaint:"#E8EEF6", navyBorder:"#B8CCE0",
  charcoal:"#1A1A1A", charMid:"#555560", charLight:"#96969F",
  border:"#D8DCE8", borderHi:"#A8B4CC",
  green:"#1B7A4A", greenBg:"#E8F5EE", greenBd:"#A8D8BC",
  red:"#B83232", redBg:"#FAECEC", redBd:"#E0AAAA",
  amber:"#C47A10", amberBg:"#FDF4E3", amberBd:"#EAC878",
  teal:"#147070", tealBg:"#E4F2F2", tealBd:"#96CCCC",
  purple:"#5A3490", purpleBg:"#F0EAF8", purpleBd:"#B89CDC",
  gold:"#A07820", goldBg:"#FAF4E4", goldBd:"#D4B464",
  slate:"#445566", slateBg:"#EEF0F4", slateBd:"#BBC4D0",
};
export const F = {
  display:"'Cormorant Garamond',Georgia,serif",
  body:"'Jost',system-ui,sans-serif",
  mono:"'DM Mono',monospace"
};

// ── Global styles ─────────────────────────────────────────────
const style = document.createElement('style');
style.textContent = `
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${C.bg};font-family:'Jost',system-ui,sans-serif;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}
  .fu{animation:fadeUp .45s ease both}
  .fi{animation:fadeIn .3s ease both}
  ::-webkit-scrollbar{width:4px}
  ::-webkit-scrollbar-thumb{background:${C.borderHi};border-radius:2px}
  input:focus,textarea:focus,select:focus{outline:2px solid ${C.navyLight};outline-offset:1px;}
  button{transition:all .15s ease;font-family:'Jost',system-ui,sans-serif;cursor:pointer;}
  .hide-scrollbar::-webkit-scrollbar{display:none;}
  .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
`;
document.head.appendChild(style);

// ── Helpers ───────────────────────────────────────────────────
export const fmtDate = d => { try { return new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); } catch { return d||''; }};
export const fmtTime = ts => { try { return new Date(ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}); } catch { return ''; }};
export const fmtMoney = n => `$${Number(n||0).toLocaleString()}`;

export async function callClaude(system, user) {
  try {
    const res = await fetch("/api/claude", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ system, user, maxTokens:900 })
    });
    const d = await res.json();
    return d.text || "No response.";
  } catch { return "Error reaching AI."; }
}

// ── Micro UI components ───────────────────────────────────────
export const Wordmark = ({size="md",light=false}) => {
  const sz = {sm:[14,9,3],md:[19,11,4],lg:[28,15,6],xl:[42,20,8]}[size]||[19,11,4];
  return (
    <div style={{display:"flex",alignItems:"baseline",gap:sz[2]}}>
      <span style={{fontFamily:F.display,fontSize:sz[0],fontWeight:700,color:light?"#fff":C.charcoal,letterSpacing:"-0.01em",lineHeight:1}}>BB</span>
      <span style={{fontFamily:F.body,fontSize:sz[1],fontWeight:600,color:light?"rgba(255,255,255,.75)":C.navy,letterSpacing:"0.16em",textTransform:"uppercase",lineHeight:1}}>Integrated</span>
    </div>
  );
};

export const Card = ({children,style={},className=""}) => (
  <div className={className} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:"0 1px 3px rgba(30,58,95,.06)",...style}}>{children}</div>
);

export const Btn = ({children,onClick,variant="ghost",disabled,small,full,loading}) => {
  const v = {
    primary:{background:C.navy,color:"#fff",border:`1px solid ${C.navy}`,fontWeight:600},
    ghost:{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`},
    outline:{background:"transparent",color:C.navy,border:`1px solid ${C.navyMid}`},
    soft:{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,fontWeight:600},
    success:{background:C.green,color:"#fff",border:`1px solid ${C.green}`,fontWeight:600},
    danger:{background:"transparent",color:C.red,border:`1px solid ${C.redBd}`},
  };
  return (
    <button onClick={onClick} disabled={disabled||loading} style={{
      ...v[variant],borderRadius:8,padding:small?"5px 13px":"10px 20px",
      fontSize:small?11:13,opacity:(disabled||loading)?.55:1,
      width:full?"100%":"auto",letterSpacing:"0.01em",
    }}>{loading?"Processing…":children}</button>
  );
};

export const Badge = ({label,type}) => {
  const m = {
    active:[C.navyFaint,C.navy,C.navyBorder],complete:[C.greenBg,C.green,C.greenBd],
    good:[C.greenBg,C.green,C.greenBd],low:[C.greenBg,C.green,C.greenBd],
    medium:[C.amberBg,C.amber,C.amberBd],high:[C.redBg,C.red,C.redBd],
    pending:[C.amberBg,C.amber,C.amberBd],signed:[C.greenBg,C.green,C.greenBd],
    pending_signature:[C.amberBg,C.amber,C.amberBd],draft:[C.slateBg,C.slate,C.slateBd],
    paid:[C.greenBg,C.green,C.greenBd],unpaid:[C.amberBg,C.amber,C.amberBd],
    overdue:[C.redBg,C.red,C.redBd],global:[C.goldBg,C.gold,C.goldBd],
    session:[C.navyFaint,C.navyMid,C.navyBorder],milestone:[C.tealBg,C.teal,C.tealBd],
    insight:[C.purpleBg,C.purple,C.purpleBd],
    Clarity:[C.amberBg,C.amber,C.amberBd],Insight:[C.purpleBg,C.purple,C.purpleBd],
    Alignment:[C.navyFaint,C.navy,C.navyBorder],Execution:[C.tealBg,C.teal,C.tealBd],
    Momentum:[C.greenBg,C.green,C.greenBd],
    universal:[C.tealBg,C.teal,C.tealBd],brian_specific:[C.purpleBg,C.purple,C.purpleBd],
  };
  const [bg,fg,bd]=m[type]||[C.navyFaint,C.navy,C.navyBorder];
  return <span style={{background:bg,color:fg,border:`1px solid ${bd}`,borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:F.body,whiteSpace:"nowrap"}}>{label}</span>;
};

export const PhaseBar = ({n,light}) => {
  const phases=["Clarity","Insight","Alignment","Execution","Momentum"];
  return (
    <div style={{display:"flex",gap:3}}>
      {phases.map((p,i)=>(
        <div key={p} style={{flex:1}}>
          <div style={{height:3,borderRadius:2,background:i<n?(light?"rgba(255,255,255,.85)":C.navy):(light?"rgba(255,255,255,.2)":C.border)}}/>
          <div style={{fontFamily:F.mono,fontSize:7,color:i<n?(light?"rgba(255,255,255,.65)":C.navy):(light?"rgba(255,255,255,.3)":C.charLight),marginTop:3,textAlign:"center"}}>{p}</div>
        </div>
      ))}
    </div>
  );
};

export const SectionHead = ({title,sub,action}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
    <div>
      <h2 style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,margin:0}}>{title}</h2>
      {sub&&<p style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:5}}>{sub}</p>}
    </div>
    {action}
  </div>
);

// ── Invite Landing Page ───────────────────────────────────────
function InviteLanding({token, onComplete}) {
  const [step,setStep]=useState("pin"); // pin | register | done
  const [client,setClient]=useState(null);
  const [pin,setPin]=useState("");
  const [firstName,setFirstName]=useState("");
  const [lastName,setLastName]=useState("");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [confirm,setConfirm]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  async function verifyPin(){
    if(pin.length!==5){setError("Please enter your 5-digit PIN.");return;}
    setLoading(true);setError("");
    const cl=await getClientByInviteToken(token);
    if(!cl){setError("Invalid invite link. Please contact your advisor.");setLoading(false);return;}
    if(cl.invite_claimed){setError("This invite has already been used. Contact your advisor to reset.");setLoading(false);return;}
    if(cl.invite_pin!==pin){setError("Incorrect PIN. Please try again.");setLoading(false);return;}
    setClient(cl);
    if(cl.temp_name){const parts=cl.temp_name.split(" ");setFirstName(parts[0]);setLastName(parts.slice(1).join(" "));}
    setStep("register");
    setLoading(false);
  }

  async function handleRegister(){
    if(!email||!password||password!==confirm){setError("Please check your email and make sure passwords match.");return;}
    if(password.length<8){setError("Password must be at least 8 characters.");return;}
    setLoading(true);setError("");
    try{
      await claimClientInvite(token,pin,{email,password,firstName,lastName});
      setStep("done");
    }catch(e){setError(e.message||"Something went wrong. Please try again.");}
    setLoading(false);
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:440}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase",marginBottom:6}}>I N T E G R O</div>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charLight}}>Client Portal</div>
        </div>
        <div style={{background:C.surface,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 32px rgba(30,58,95,.12)"}}>
          <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,padding:"20px 28px"}}>
            <div style={{fontFamily:F.display,fontSize:20,color:"#fff",fontWeight:600,marginBottom:2}}>
              {step==="pin"?"Welcome to your portal":step==="register"?"Create your login":step==="done"?"You're all set!":""}
            </div>
            <div style={{fontFamily:F.body,fontSize:12,color:"rgba(255,255,255,.6)"}}>
              {step==="pin"?"Enter your PIN to get started":step==="register"?"Set up your email and password":step==="done"?"Your account is ready":""}
            </div>
          </div>
          <div style={{padding:"24px 28px"}}>
            {error&&<div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"8px 12px",marginBottom:14,fontFamily:F.body,fontSize:12,color:C.red}}>{error}</div>}

            {step==="pin"&&(
              <>
                <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Your 5-Digit PIN</label>
                <input type="password" value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,"").slice(0,5))} onKeyDown={e=>e.key==="Enter"&&verifyPin()} maxLength={5} placeholder="•••••"
                  style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",color:C.charcoal,fontFamily:F.mono,fontSize:24,letterSpacing:".3em",textAlign:"center",boxSizing:"border-box",marginBottom:16}}/>
                <button onClick={verifyPin} disabled={loading||pin.length!==5} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading||pin.length!==5?.6:1}}>
                  {loading?"Verifying…":"Continue →"}
                </button>
              </>
            )}

            {step==="register"&&(
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>First Name</label>
                    <input value={firstName} onChange={e=>setFirstName(e.target.value)} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Last Name</label>
                    <input value={lastName} onChange={e=>setLastName(e.target.value)} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Email Address</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Create Password</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Confirm Password</label>
                  <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleRegister()} placeholder="Re-enter password"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <button onClick={handleRegister} disabled={loading} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading?.6:1}}>
                  {loading?"Creating account…":"Create Account & Sign In →"}
                </button>
                <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,textAlign:"center",marginTop:10}}>Your browser will offer to save your login. We recommend allowing it.</div>
              </>
            )}

            {step==="done"&&(
              <div style={{textAlign:"center",padding:"12px 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>✅</div>
                <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,marginBottom:6,fontWeight:500}}>Account created successfully!</div>
                <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:20}}>You can now sign in with your email and password anytime.</div>
                <button onClick={()=>window.location.href='/'} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"11px 28px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>Go to My Portal →</button>
              </div>
            )}
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
      </div>
    </div>
  );
}

// ── Advisor Invite Landing Page ───────────────────────────────
function InviteLandingAdvisor({token, onComplete}) {
  const [step,setStep]=useState("pin"); // pin | register | done
  const [advisor,setAdvisor]=useState(null);
  const [pin,setPin]=useState("");
  const [firstName,setFirstName]=useState("");
  const [lastName,setLastName]=useState("");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [confirm,setConfirm]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  async function verifyPin(){
    if(pin.length!==5){setError("Please enter your 5-digit PIN.");return;}
    setLoading(true);setError("");
    const adv=await getAdvisorByInviteToken(token);
    if(!adv){setError("Invalid invite link. Please contact the master administrator.");setLoading(false);return;}
    if(adv.invite_claimed){setError("This invite has already been used. Contact the master to reset.");setLoading(false);return;}
    if(adv.invite_pin!==pin){setError("Incorrect PIN. Please try again.");setLoading(false);return;}
    setAdvisor(adv);
    if(adv.temp_name){const parts=adv.temp_name.split(" ");setFirstName(parts[0]);setLastName(parts.slice(1).join(" "));}
    if(adv.temp_email){setEmail(adv.temp_email);}
    setStep("register");
    setLoading(false);
  }

  async function handleRegister(){
    if(!email||!password||password!==confirm){setError("Please check your email and make sure passwords match.");return;}
    if(password.length<8){setError("Password must be at least 8 characters.");return;}
    setLoading(true);setError("");
    try{
      await claimAdvisorInvite(token,pin,{email,password,firstName,lastName});
      setStep("done");
    }catch(e){setError(e.message||"Something went wrong. Please try again.");}
    setLoading(false);
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:440}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase",marginBottom:6}}>I N T E G R O</div>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charLight}}>Advisor Portal</div>
        </div>
        <div style={{background:C.surface,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 32px rgba(30,58,95,.12)"}}>
          <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,padding:"20px 28px"}}>
            <div style={{fontFamily:F.display,fontSize:20,color:"#fff",fontWeight:600,marginBottom:2}}>
              {step==="pin"?"Welcome, Advisor":step==="register"?"Create your login":step==="done"?"You're all set!":""}
            </div>
            <div style={{fontFamily:F.body,fontSize:12,color:"rgba(255,255,255,.6)"}}>
              {step==="pin"?"Enter your PIN to get started":step==="register"?"Set up your email and password":step==="done"?"Your account is ready":""}
            </div>
          </div>
          <div style={{padding:"24px 28px"}}>
            {error&&<div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"8px 12px",marginBottom:14,fontFamily:F.body,fontSize:12,color:C.red}}>{error}</div>}

            {step==="pin"&&(
              <>
                <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Your 5-Digit PIN</label>
                <input type="password" value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,"").slice(0,5))} onKeyDown={e=>e.key==="Enter"&&verifyPin()} maxLength={5} placeholder="•••••"
                  style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",color:C.charcoal,fontFamily:F.mono,fontSize:24,letterSpacing:".3em",textAlign:"center",boxSizing:"border-box",marginBottom:16}}/>
                <button onClick={verifyPin} disabled={loading||pin.length!==5} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading||pin.length!==5?.6:1}}>
                  {loading?"Verifying…":"Continue →"}
                </button>
              </>
            )}

            {step==="register"&&(
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>First Name</label>
                    <input value={firstName} onChange={e=>setFirstName(e.target.value)} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Last Name</label>
                    <input value={lastName} onChange={e=>setLastName(e.target.value)} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Email Address</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@bbintegrated.com"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Create Password</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Confirm Password</label>
                  <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleRegister()} placeholder="Re-enter password"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <button onClick={handleRegister} disabled={loading} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading?.6:1}}>
                  {loading?"Creating account…":"Create Account & Sign In →"}
                </button>
                <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,textAlign:"center",marginTop:10}}>Your browser will offer to save your login. We recommend allowing it.</div>
              </>
            )}

            {step==="done"&&(
              <div style={{textAlign:"center",padding:"12px 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>✅</div>
                <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,marginBottom:6,fontWeight:500}}>Account created successfully!</div>
                <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:20}}>You can now sign in with your email and password anytime.</div>
                <button onClick={()=>window.location.href='/'} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"11px 28px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>Go to My Dashboard →</button>
              </div>
            )}
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
      </div>
    </div>
  );
}

// ── Team Member Invite Landing (Phase 4) ──────────────────────
// Real claim flow at /join/team/<token>. Mirrors the client/advisor invite
// pages with a small teal "Team Member" accent. Steps:
//   loading → pin → register → done (redirects to /welcome/team)
//                 → locked (after 5 failed PIN attempts; DB-backed)
//                 → invalid (token doesn't match an unclaimed invite)
function TeamInviteLanding({token}) {
  const [step, setStep] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [pin, setPin] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      const p = await getTeamMemberProfileByInviteToken(token);
      if (!alive) return;
      if (!p) { setStep("invalid"); return; }
      setProfile(p);
      setStep(p.claim_locked_at ? "locked" : "pin");
    })();
    return () => { alive = false; };
  }, [token]);

  async function verifyPin() {
    if (pin.length !== 6) { setError("Please enter your 6-digit PIN."); return; }
    if (!profile) return;
    setLoading(true); setError("");
    try {
      const result = await verifyTeamMemberPin(profile, pin);
      setProfile(result.profile);
      if (result.locked) {
        setStep("locked");
      } else if (result.ok) {
        // Pre-fill name from the invite record.
        const fullName = (result.profile.name || "").trim();
        const idx = fullName.indexOf(" ");
        setFirstName(idx >= 0 ? fullName.slice(0, idx) : fullName);
        setLastName(idx >= 0 ? fullName.slice(idx + 1) : "");
        setStep("register");
      } else {
        const remaining = result.attemptsRemaining ?? 0;
        setError(remaining > 0
          ? `Incorrect PIN. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
          : "Incorrect PIN.");
        setPin("");
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  async function handleRegister() {
    if (!firstName.trim()) { setError("Please enter your first name."); return; }
    if (!password || password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError("");
    try {
      await claimTeamMemberInvite(profile, {
        email: profile.email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        pin,  // verified server-side by the RPC (defense in depth)
      });
      // Hard navigation so App.js re-mounts with the new auth session
      // and routes to the welcome page.
      window.location.href = '/welcome/team';
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  const headerTitle =
    step === "loading"  ? "Loading…"
  : step === "pin"      ? "Welcome to the portal"
  : step === "register" ? "Confirm your name & set a password"
  : step === "locked"   ? "This invite is locked"
  : step === "invalid"  ? "Invite not found" : "";
  const headerSub =
    step === "pin"      ? "Enter your 6-digit PIN to get started"
  : step === "register" ? "Almost there"
  : step === "locked"   ? "Too many incorrect attempts"
  : step === "invalid"  ? "Double-check your invite link" : "";

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:440}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase",marginBottom:10}}>I N T E G R O</div>
          <span style={{display:"inline-block",background:C.tealBg,color:C.teal,border:`1px solid ${C.tealBd}`,borderRadius:12,padding:"3px 12px",fontFamily:F.body,fontSize:9,fontWeight:700,letterSpacing:".15em",textTransform:"uppercase"}}>Team Member</span>
        </div>
        <div style={{background:C.surface,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 32px rgba(30,58,95,.12)"}}>
          <div style={{height:3,background:C.teal}}/>
          <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,padding:"20px 28px"}}>
            <div style={{fontFamily:F.display,fontSize:20,color:"#fff",fontWeight:600,marginBottom:2}}>{headerTitle}</div>
            <div style={{fontFamily:F.body,fontSize:12,color:"rgba(255,255,255,.6)"}}>{headerSub}</div>
          </div>
          <div style={{padding:"24px 28px"}}>
            {error && <div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"8px 12px",marginBottom:14,fontFamily:F.body,fontSize:12,color:C.red}}>{error}</div>}

            {step === "loading" && (
              <div style={{padding:"22px 0",textAlign:"center",fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>Loading your invite…</div>
            )}

            {step === "invalid" && (
              <div style={{padding:"6px 0",textAlign:"center"}}>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:8}}>This invite link doesn't match any active invite.</div>
                <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:18,lineHeight:1.5}}>It may have already been claimed, expired, or been mistyped. Contact whoever invited you for a new link.</div>
                <button onClick={()=>window.location.href='/'} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 22px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>← Back to home</button>
              </div>
            )}

            {step === "locked" && (
              <div style={{padding:"6px 0"}}>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:10,lineHeight:1.55}}>
                  This invite has been locked after too many incorrect PIN attempts.
                </div>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginBottom:18,lineHeight:1.55}}>
                  Contact <strong style={{color:C.charcoal}}>{profile?.invited_by_name || "your inviter"}</strong> for a new invite.
                </div>
                <button onClick={()=>window.location.href='/'} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 22px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>← Back to home</button>
              </div>
            )}

            {step === "pin" && (
              <>
                <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Your 6-Digit PIN</label>
                <input type="password" value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&verifyPin()} maxLength={6} placeholder="••••••"
                  style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",color:C.charcoal,fontFamily:F.mono,fontSize:24,letterSpacing:".3em",textAlign:"center",boxSizing:"border-box",marginBottom:16}}/>
                <button onClick={verifyPin} disabled={loading||pin.length!==6} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading||pin.length!==6?.6:1}}>
                  {loading ? "Verifying…" : "Continue →"}
                </button>
              </>
            )}

            {step === "register" && (
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>First Name</label>
                    <input value={firstName} onChange={e=>setFirstName(e.target.value)}
                      style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Last Name</label>
                    <input value={lastName} onChange={e=>setLastName(e.target.value)}
                      style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Email</label>
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charLight,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}>{profile?.email}</div>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Create Password</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Confirm Password</label>
                  <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleRegister()} placeholder="Re-enter password"
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <button onClick={handleRegister} disabled={loading} style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer",opacity:loading?.6:1}}>
                  {loading ? "Creating account…" : "Create Account & Sign In →"}
                </button>
                <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,textAlign:"center",marginTop:10}}>Your browser will offer to save your login. We recommend allowing it.</div>
              </>
            )}
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
      </div>
    </div>
  );
}

// ── Team Member Welcome (Phase 4) ─────────────────────────────
// One-time landing at /welcome/team after a successful claim. If
// welcome_seen_at is already set, redirects straight to portal Overview.
function TeamWelcome() {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await getMyTeamMemberContext();
      if (!alive) return;
      if (!data) {
        // Not authenticated as a team member — bounce to home.
        window.location.href = '/';
        return;
      }
      if (data.profile.welcome_seen_at) {
        // Already seen — straight to portal.
        window.location.href = '/';
        return;
      }
      setCtx(data);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  async function enterPortal() {
    setEntering(true);
    try {
      await markTeamMemberWelcomeSeen(ctx.profile.id);
    } catch (e) {
      console.warn('[team-welcome] mark seen failed:', e?.message || e);
    }
    window.location.href = '/';
  }

  if (loading || !ctx) return <Loading/>;

  const firstName = ((ctx.profile.name || "").trim().split(/\s+/)[0]) || "there";
  const companyName = ctx.client?.company || "your team";

  // Build a friendly access summary from page_access + team_member_access
  // counts. Avoids querying projects/files/etc directly (Phase 5 territory).
  const PAGE_TO_RESOURCE_TYPE = { projects:'project', files:'file', docs:'doc', resources:'resource', billing:'invoice' };
  const PAGE_LABEL_SINGULAR  = { projects:'project', files:'file', docs:'doc', resources:'resource', billing:'invoice' };
  const pa = ctx.profile.page_access || [];
  const parts = [];
  ['projects','files','docs','resources','billing'].forEach(k => {
    if (!pa.includes(k)) return;
    const count = ctx.itemCounts[PAGE_TO_RESOURCE_TYPE[k]] || 0;
    if (count === 0) return;
    const lbl = PAGE_LABEL_SINGULAR[k];
    parts.push(`${count} ${lbl}${count === 1 ? "" : "s"}`);
  });
  if (pa.includes('assessment')) parts.push("the assessment");

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:560}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase",marginBottom:10}}>I N T E G R O</div>
          <span style={{display:"inline-block",background:C.tealBg,color:C.teal,border:`1px solid ${C.tealBd}`,borderRadius:12,padding:"3px 12px",fontFamily:F.body,fontSize:9,fontWeight:700,letterSpacing:".15em",textTransform:"uppercase"}}>Team Member</span>
        </div>
        <div style={{background:C.surface,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 32px rgba(30,58,95,.12)"}}>
          <div style={{height:3,background:C.teal}}/>
          <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,padding:"22px 30px"}}>
            <div style={{fontFamily:F.display,fontSize:24,color:"#fff",fontWeight:600,marginBottom:3}}>Welcome, {firstName}.</div>
            <div style={{fontFamily:F.body,fontSize:13,color:"rgba(255,255,255,.7)"}}>You've been brought into {companyName}'s work.</div>
          </div>
          <div style={{padding:"26px 30px"}}>
            <p style={{fontFamily:F.body,fontSize:14,color:C.charcoal,lineHeight:1.65,marginTop:0,marginBottom:18}}>
              You've been given a real seat at the table — not as an observer, but as someone trusted to help carry the work forward. The portal isn't a dashboard. It's the workspace where the actual decisions are made, and you've been given access to the parts where your perspective matters most.
            </p>

            <div style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 18px",marginBottom:22}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>You have access to</div>
              {parts.length === 0 ? (
                <div style={{fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>
                  No specific items have been shared with you yet — your inviter will add things as the work develops.
                </div>
              ) : (
                <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,lineHeight:1.5}}>
                  {parts.join(" · ")}
                </div>
              )}
            </div>

            <button onClick={enterPortal} disabled={entering}
              style={{width:"100%",background:C.navy,color:"#fff",border:"none",borderRadius:10,padding:"14px",fontFamily:F.body,fontSize:14,fontWeight:600,cursor:entering?"not-allowed":"pointer",opacity:entering?.6:1}}>
              {entering ? "Entering…" : "Enter the Portal →"}
            </button>
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
      </div>
    </div>
  );
}

// ── Loading screen ────────────────────────────────────────────
const Loading = () => (
  <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
    <span style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:20,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase"}}>I N T E G R O</span>
    <div style={{fontFamily:F.mono,fontSize:11,color:C.charLight,animation:"pulse 1.5s infinite"}}>Loading your portal…</div>
  </div>
);

// ── Login screen ──────────────────────────────────────────────
function Login({onLogin, onStartOnboarding}) {
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [showPass,setShowPass]=useState(false);
  const [error,setError]=useState("");
  const [loading,setLoading]=useState(false);

  async function attempt() {
    setError(""); setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
      if (error) throw error;
      const user = data.user;
      // Build profile immediately from auth data — no DB query needed
      const userEmail = user.email || '';
      let role = 'client';
      if (userEmail === 'brian@bbintegrated.com') role = 'master';
      else if (userEmail.endsWith('@bbintegrated.com')) role = 'sales';
      const profile = {
        id: user.id,
        email: user.email,
        name: userEmail.split('@')[0],
        role,
        tier: 'free',
      };
      onLogin(profile);
    } catch(e) {
      setError("Email or password not recognized. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",width:700,height:700,borderRadius:"50%",background:`radial-gradient(circle,${C.navyFaint} 0%,transparent 65%)`,top:-280,left:-280,pointerEvents:"none"}}/>
      <div className="fu" style={{width:440,position:"relative",zIndex:1}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase",lineHeight:1,marginBottom:8}}>I N T E G R O</div>
          <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,letterSpacing:".1em"}}>Client Portal</div>
        </div>
        <Card style={{overflow:"hidden",boxShadow:"0 16px 48px rgba(30,58,95,.12)"}}>
          <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,padding:"20px 28px"}}>
            <div style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:14,fontWeight:200,color:"rgba(255,255,255,.85)",letterSpacing:".38em",textTransform:"uppercase",marginBottom:6}}>I N T E G R O</div>
            <div style={{fontFamily:F.display,fontSize:20,color:"#fff",fontWeight:600,marginBottom:2}}>Welcome back.</div>
            <div style={{fontFamily:F.body,fontSize:12,color:"rgba(255,255,255,.6)"}}>Sign in to access your portal.</div>
          </div>
          <div style={{padding:"26px 30px 30px"}}>
            <div style={{marginBottom:14}}>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase"}}>Email</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e=>e.key==="Enter"&&attempt()}
                style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 13px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:6,fontWeight:600,letterSpacing:".07em",textTransform:"uppercase"}}>Password</label>
              <div style={{position:"relative"}}>
                <input type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="Your password" onKeyDown={e=>e.key==="Enter"&&attempt()}
                  style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 40px 11px 13px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                <button type="button" onClick={()=>setShowPass(s=>!s)} aria-label={showPass?"Hide password":"Show password"} tabIndex={-1}
                  style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",padding:6,cursor:"pointer",color:C.charLight,display:"flex",alignItems:"center",lineHeight:0}}>
                  {showPass?<EyeOff size={16}/>:<Eye size={16}/>}
                </button>
              </div>
            </div>
            {error&&<div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:"9px 13px",fontFamily:F.body,fontSize:12,color:C.red,marginBottom:14,textAlign:"center"}}>{error}</div>}
            <Btn variant="primary" full onClick={attempt} loading={loading} disabled={!email||!pass}>Sign In</Btn>
            <div style={{display:"flex",alignItems:"center",gap:12,margin:"18px 0"}}>
              <div style={{flex:1,height:1,background:C.border}}/>
              <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>New to Integro?</span>
              <div style={{flex:1,height:1,background:C.border}}/>
            </div>
            <button onClick={onStartOnboarding} style={{width:"100%",background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 0",fontFamily:F.body,fontSize:13,fontWeight:600}}>
              Get Started — Free Assessment →
            </button>
          </div>
        </Card>
        <div style={{textAlign:"center",marginTop:18,fontFamily:F.mono,fontSize:9,color:C.charLight}}>a BB Integrated product</div>
      </div>
    </div>
  );
}

// ── withTimeout helper ────────────────────────────────────────
// Wraps a promise with a deadline that cleanly cancels its timer when the
// underlying call settles first. Promise.race by itself does NOT cancel
// losing branches — the orphan setTimeout still fires and surfaces as an
// "Uncaught (in promise) Error: timeout" in the console. This pattern
// captures the timer id and clearTimeout's it in .finally(), so the timer
// is dead the instant the race is decided (either way). Use this anywhere
// a Promise.race([call, setTimeout-reject]) pattern would otherwise create
// an orphan timer.
function withTimeout(promise, ms, label) {
  let timerId;
  const timeout = new Promise((_, reject) => {
    timerId = setTimeout(() => reject(new Error(label || 'timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timerId));
}

// ── Root App ──────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('login'); // login | onboarding | portal
  const [clients, setClients] = useState([]);
  const [viewingClient, setViewingClient] = useState(null);

  // Check for existing session on load
  useEffect(() => {
    checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkSession() {
    try {
      // 5s deadline — falls through to login if auth lookup hangs.
      const profile = await withTimeout(getCurrentUser(), 5000);
      if (profile) {
        setUser(profile);
        await loadClientsForUser(profile);
        setView('portal');
      }
    } catch(e) {
      // Timeout or no session — just show login screen
      console.log('No active session, showing login.');
    }
    setLoading(false);
  }

  async function loadClientsForUser(profile) {
    // Timeout is created per-branch (not before the role check) so roles
    // that don't fetch clients — e.g. team_member — don't spawn an
    // orphan timer that fires unobserved after 8s.
    try {
      if (profile.role === 'master') {
        const data = await withTimeout(getAllClients(), 8000);
        setClients(data || []);
      } else if (profile.role === 'sales') {
        const data = await withTimeout(getClientsForAdvisor(profile.id), 8000);
        setClients(data || []);
      } else if (profile.role === 'client') {
        const data = await withTimeout(getClientForUser(profile.id), 8000);
        setClients(data ? [data] : []);
      } else if (profile.role === 'team_member') {
        const ctx = await withTimeout(getMyTeamMemberContext(), 8000);
        setClients(ctx && ctx.client ? [{...ctx.client, _teamMemberPageAccess: ctx.profile?.page_access || []}] : []);
      }
    } catch(e) {
      console.warn('Failed to load clients:', e.message);
      setClients([]);
    }
  }

  async function handleLogin(profile) {
    // Re-fetch the authoritative profile from the DB so we don't render the
    // wrong dashboard if the Login component handed us a fallback/email-guessed
    // role. Falls back to the passed-in profile if the lookup fails.
    let canonical = profile;
    try {
      const fresh = await getCurrentUser();
      if (fresh) canonical = fresh;
    } catch(e) {
      // fall back to passed-in profile if the lookup fails
    }
    setUser(canonical);
    setView('portal');
    loadClientsForUser(canonical);
  }

  async function handleLogout() {
    await signOut();
    setUser(null);
    setClients([]);
    setViewingClient(null);
    setView('login');
  }

  // Path-based routes (invites + welcome) — these are full-page experiences
  // with their own loading states, so they render before the app-init gate.
  // Phase 4 hoisted these above the gate so /welcome/team renders fast for
  // freshly-authenticated team members (loadClientsForUser may take seconds).
  const path = window.location.pathname;
  if (path.startsWith('/join/advisor/')) {
    const token = path.replace('/join/advisor/', '');
    return <InviteLandingAdvisor token={token} onComplete={()=>window.location.href='/'}/>
  }
  if (path.startsWith('/welcome/team')) {
    return <TeamWelcome/>;
  }
  if (path.startsWith('/join/team/')) {
    const token = path.replace('/join/team/', '');
    return <TeamInviteLanding token={token}/>;
  }
  if (path.startsWith('/join/')) {
    const token = path.replace('/join/', '');
    return <InviteLanding token={token} onComplete={()=>window.location.href='/'}/>
  }

  if (loading) return <Loading />;

  // Import portal views dynamically based on user role
  // For now show a placeholder — full portal imported from Portal.js
  if (view === 'login' || !user) {
    return <Login onLogin={handleLogin} onStartOnboarding={() => setView('onboarding')} />;
  }

  // Portal view — passes user, clients, and handlers down
  return (
    <PortalShell
      user={user}
      clients={clients}
      viewingClient={viewingClient}
      setViewingClient={setViewingClient}
      onLogout={handleLogout}
      reloadClients={() => loadClientsForUser(user)}
    />
  );
}

// ── Portal Shell — routes by role ─────────────────────────────
function PortalShell({ user, clients, viewingClient, setViewingClient, onLogout, reloadClients }) {
  const [previewMode, setPreviewMode] = useState(false);
  const [viewingAsAdvisor, setViewingAsAdvisor] = useState(null);
  // Optional chrome section to land on when entering a client's portal.
  // Set by dashboard buttons that want to open the portal already on a
  // non-default section (e.g. "Manage" → "admin"). Cleared on back.
  const [initialSection, setInitialSection] = useState(null);

  function handleViewClient(cl, preview=false, initial=null){
    setViewingClient(cl);
    setPreviewMode(preview);
    setInitialSection(initial);
  }

  function handleViewAdvisor(adv){
    setViewingAsAdvisor(adv);
  }

  // Master admin view.
  // viewingClient is checked BEFORE viewingAsAdvisor so that clicking Enter
  // Portal / Preview on a client while impersonating an advisor actually
  // routes into the client portal. viewingAsAdvisor stays set in the
  // background, so onBack (which only clears viewingClient) falls through
  // to the AdvisorView render below, returning the master to the
  // impersonated advisor view rather than all the way out.
  if (user.role === 'master') {
    if (viewingClient) {
      const previewName = viewingClient.user?.name||viewingClient.temp_name||"the client";
      return (
        <ClientPortalContent
          client={viewingClient}
          isAdvisor={!previewMode}
          user={previewMode?{...viewingClient.user,role:"client"}:user}
          isImpersonating={true}
          reloadClients={reloadClients}
          advisorId={user.id}
          operatorRole={user.role}
          initialChromeSection={initialSection}
          onBack={()=>{setViewingClient(null);setPreviewMode(false);setInitialSection(null);}}
          onLogout={onLogout}
          banner={previewMode ? <ImpersonationBanner label={`Previewing as Client — this is exactly what ${previewName} sees`} exitLabel="Exit Preview" onExit={()=>setPreviewMode(false)}/> : null}
        />
      );
    }
    if (viewingAsAdvisor) {
      const advisorName = viewingAsAdvisor.user?.name || viewingAsAdvisor.temp_name || "Advisor";
      const advisorClients = clients.filter(c => c.advisor_id === viewingAsAdvisor.user_id);
      return (
        <AdvisorView
          user={{...viewingAsAdvisor.user, role:"sales"}}
          clients={advisorClients}
          isImpersonating={true}
          onViewClient={handleViewClient}
          onLogout={onLogout}
          reloadClients={reloadClients}
          banner={<ImpersonationBanner label={`Viewing as ${advisorName}`} exitLabel="Exit" onExit={()=>setViewingAsAdvisor(null)}/>}
        />
      );
    }
    return <MasterView user={user} clients={clients} isImpersonating={false} onViewClient={handleViewClient} onViewAdvisor={handleViewAdvisor} onLogout={onLogout} reloadClients={reloadClients}/>;
  }

  // Sales advisor view
  if (user.role === 'sales') {
    if (viewingClient) {
      const previewName = viewingClient.user?.name||viewingClient.temp_name||"the client";
      return (
        <ClientPortalContent
          client={viewingClient}
          isAdvisor={!previewMode}
          user={previewMode?{...viewingClient.user,role:"client"}:user}
          isImpersonating={true}
          reloadClients={reloadClients}
          advisorId={user.id}
          operatorRole={user.role}
          initialChromeSection={initialSection}
          onBack={()=>{setViewingClient(null);setPreviewMode(false);setInitialSection(null);}}
          onLogout={onLogout}
          banner={previewMode ? <ImpersonationBanner label={`Previewing as Client — this is exactly what ${previewName} sees`} exitLabel="Exit Preview" onExit={()=>setPreviewMode(false)}/> : null}
        />
      );
    }
    return <AdvisorView user={user} clients={clients} isImpersonating={false} onViewClient={handleViewClient} onLogout={onLogout} reloadClients={reloadClients}/>;
  }

  // Client view
  if (user.role === 'client') {
    const clientData = clients[0];
    if (!clientData) return (
      <div style={{padding:40,fontFamily:F.body,color:C.charcoal,textAlign:"center"}}>
        <div style={{marginBottom:16}}><span style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase"}}>I N T E G R O</span></div>
        <p>Client profile not found. Please contact your advisor.</p>
        <button onClick={onLogout} style={{marginTop:16,background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"10px 20px",fontFamily:F.body,fontSize:13,fontWeight:600}}>Sign Out</button>
      </div>
    );
    return <ClientPortalContent client={clientData} isAdvisor={false} user={user} reloadClients={reloadClients} onLogout={onLogout}/>;
  }

  // Team member view
  if (user.role === 'team_member') {
    const clientData = clients[0];
    if (!clientData) return (
      <div style={{padding:40,fontFamily:F.body,color:C.charcoal,textAlign:"center"}}>
        <div style={{marginBottom:16}}><span style={{fontFamily:"'Josefin Sans',sans-serif",fontSize:18,fontWeight:200,color:C.navy,letterSpacing:".42em",textTransform:"uppercase"}}>I N T E G R O</span></div>
        <p>No client access found. Please contact your client administrator.</p>
        <button onClick={onLogout} style={{marginTop:16,background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"10px 20px",fontFamily:F.body,fontSize:13,fontWeight:600}}>Sign Out</button>
      </div>
    );
    return <ClientPortalContent client={clientData} isAdvisor={false} user={user} reloadClients={reloadClients} onLogout={onLogout} pageAccess={clientData._teamMemberPageAccess || []}/>;
  }

  return null;
}

// ── Mobile client card ────────────────────────────────────────
// Shared between MasterView and AdvisorView clients sections. Mirrors the
// data shown on the desktop list/grid templates with a touch-friendly
// action stack: Enter Portal hero on top, Preview + Manage paired below.
// showAdvisorPill is only true for the Master cross-advisor tab; defaults
// falsy in AdvisorView and on the Master "master" tab.
function ClientCard({ cl, activity, showAdvisorPill, onViewClient }) {
  const count = activity[cl.id]?.count || 0;
  const latest = activity[cl.id]?.latest;
  const projectCount = (cl.projects || []).length;
  const revenue = (cl.invoices || []).filter(inv => inv.status === "paid").reduce((a, inv) => a + Number(inv.amount), 0);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: F.display, fontSize: 16, color: C.charcoal, fontWeight: 600 }}>
          {cl.user?.name || cl.temp_name || "Client"}
        </div>
        {count > 0 && (
          <div title={`${count} update${count > 1 ? "s" : ""} in last 7 days`}
            style={{ background: C.red, color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.mono, fontSize: 8, fontWeight: 700, flexShrink: 0 }}>
            {count}
          </div>
        )}
        <Badge label={cl.assessment_complete ? "Assessment done" : "Assessment pending"} type={cl.assessment_complete ? "complete" : "pending"} />
        {showAdvisorPill && cl.advisor && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: C.purpleBg, color: C.purple, border: `1px solid ${C.purpleBd}`, borderRadius: 10, padding: '2px 8px', fontFamily: F.body, fontSize: 10, fontWeight: 600 }}>
            <span>⊙</span><span>{cl.advisor?.name || cl.advisor?.email || "Unknown advisor"}</span>
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontFamily: F.body, fontSize: 12, color: C.charMid, marginBottom: 14 }}>
        <span>{cl.company}</span>
        {latest && <span style={{ fontFamily: F.mono, fontSize: 10, color: C.navyMid }}>Latest: {fmtDate(latest)}</span>}
        <span><span style={{ color: C.charLight }}>Projects </span><span style={{ color: C.charcoal, fontWeight: 600 }}>{projectCount}</span></span>
        <span><span style={{ color: C.charLight }}>Revenue </span><span style={{ color: C.green, fontWeight: 600 }}>{fmtMoney(revenue)}</span></span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ActionButton variant="primary" label="Enter Portal" onClick={() => onViewClient(cl, false)} block size="lg" />
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton variant="warning" label="Preview" onClick={() => onViewClient(cl, true)} block size="lg" />
          <ActionButton variant="secondary" label="Manage" onClick={() => onViewClient(cl, false, "admin")} block size="lg" />
        </div>
      </div>
    </div>
  );
}

// ── Master admin view ─────────────────────────────────────────
function MasterView({user, clients, isImpersonating=false, onViewClient, onViewAdvisor, onLogout, reloadClients}) {
  const nav = useNavState("clients");
  const isMobile = useIsMobile();
  const [showAddClient,setShowAddClient]=useState(false);
  const [activity,setActivity]=useState({});
  const [viewMode,setViewMode]=useState("list");
  const [filterByActivity,setFilterByActivity]=useState(false);
  const [clientTab,setClientTab]=useState("master"); // "master" | "advisor"
  const [advisorFilter,setAdvisorFilter]=useState("all"); // "all" | advisor user_id
  const [advisors,setAdvisors]=useState([]);
  const [showAddAdvisor,setShowAddAdvisor]=useState(false);
  const [newAdvisor,setNewAdvisor]=useState({firstName:"",lastName:"",email:"",pin:""});
  const [addingAdvisor,setAddingAdvisor]=useState(false);
  const [newAdvisorInviteUrl,setNewAdvisorInviteUrl]=useState(null);
  const [copiedAdvisor,setCopiedAdvisor]=useState(false);
  const [copiedRowToken,setCopiedRowToken]=useState(null);
  const [resendAdvisorId,setResendAdvisorId]=useState(null); // which advisor's resend panel is open
  const [copiedResendId,setCopiedResendId]=useState(null);   // "Copied!" flash on the panel's Copy Message button

  useEffect(()=>{
    const ids=clients.map(c=>c.id).filter(Boolean);
    if(ids.length>0){
      getRecentActivityForClients(ids).then(setActivity).catch(()=>{});
    }
  },[clients]);

  useEffect(()=>{loadAdvisors();},[]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalProjects=clients.reduce((a,c)=>a+(c.projects?.length||0),0);
  const assessmentsDone=clients.filter(c=>c.assessment_complete).length;
  const totalRevenue=clients.reduce((a,c)=>a+(c.invoices||[]).filter(i=>i.status==="paid").reduce((s,i)=>s+Number(i.amount),0),0);
  const outstanding=clients.reduce((a,c)=>a+(c.invoices||[]).filter(i=>i.status!=="paid").reduce((s,i)=>s+Number(i.amount),0),0);
  const activeClientCount=clients.filter(c=>activity[c.id]?.count>0).length;
  const visibleClients=filterByActivity?clients.filter(c=>activity[c.id]?.count>0):clients;

  // "Tabs by advisor": split visibleClients into rows the master owns vs.
  // rows assigned to other advisors. Advisor display name comes from the
  // joined `advisor` object on each client (see getAllClients in supabase.js
  // — selects advisor:users!clients_advisor_id_fkey(id, name, email)).
  const masterClients=visibleClients.filter(c=>c.advisor_id===user.id);
  const advisorClients=visibleClients.filter(c=>c.advisor_id!==user.id);
  const advisorList=Array.from(
    new Map(
      advisorClients.map(c=>[c.advisor_id,{id:c.advisor_id,name:c.advisor?.name||c.advisor?.email||c.advisor_id}])
    ).values()
  );
  const tabFilteredClients=clientTab==="master"
    ? masterClients
    : (advisorFilter==="all"?advisorClients:advisorClients.filter(c=>c.advisor_id===advisorFilter));

  async function loadAdvisors(){
    try{
      const data=await getAllAdvisors();
      setAdvisors(data||[]);
    }catch(e){console.warn("Failed to load advisors:",e.message);}
  }

  async function handleAddAdvisor(){
    if(!newAdvisor.firstName||!newAdvisor.email||newAdvisor.pin.length!==5) return;
    setAddingAdvisor(true);
    try{
      const adv=await createAdvisorWithPin(user.id,{
        firstName:newAdvisor.firstName,lastName:newAdvisor.lastName,
        email:newAdvisor.email,pin:newAdvisor.pin,
      });
      const url=`${window.location.origin}/join/advisor/${adv.invite_token}`;
      setNewAdvisorInviteUrl(url);
      setNewAdvisor({firstName:"",lastName:"",email:"",pin:""});
      await loadAdvisors();
    }catch(e){alert("Failed to create advisor: "+e.message);}
    setAddingAdvisor(false);
  }

  function copyAdvisorUrl(){
    navigator.clipboard.writeText(newAdvisorInviteUrl);
    setCopiedAdvisor(true);setTimeout(()=>setCopiedAdvisor(false),2000);
  }

  // Pure read of the existing advisor row — no DB writes. The advisor's
  // invite_token + invite_pin already live on the row (getAllAdvisors does
  // select *). First name is derived from temp_name; falls back to the
  // joined user name (if claimed somehow) or "there".
  function buildAdvisorInviteMessage(adv){
    const firstName=((adv.temp_name||adv.user?.name||"").trim().split(/\s+/)[0])||"there";
    const url=`${window.location.origin}/join/advisor/${adv.invite_token}`;
    const pin=adv.invite_pin||"—";
    return `Hi ${firstName},

You've been invited to join Integro as an advisor — your access to manage your clients, projects, and files in one place.

GAIN ACCESS:
Click the link below to enter your PIN, then verify your details and set your own password.

Link: ${url}
PIN: ${pin}

Once you're in, you'll be able to set up and work with your clients directly. Reach out if you have any questions.

— The Integro Team`;
  }

  function copyResendMessage(advId, text){
    navigator.clipboard.writeText(text);
    setCopiedResendId(advId);
    setTimeout(()=>setCopiedResendId(prev=>prev===advId?null:prev),2000);
  }

  function copyRowUrl(token){
    const url=`${window.location.origin}/join/advisor/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedRowToken(token);
    setTimeout(()=>setCopiedRowToken(null),2000);
  }

  const sidebarItems = [
    { id:"clients",   icon:Users,    label:"Clients" },
    { id:"advisors",  icon:Compass,  label:"Advisors" },
    { id:"knowledge", icon:BookOpen, label:"Knowledge Base" },
  ];

  // knowledge intentionally omitted — KnowledgeBaseTab renders its own header.
  const headerByTab = {
    clients:  { title:"Master Dashboard", subtitle:"Master view across all advisors and clients." },
    advisors: { title:"Advisors",         subtitle:"Advisors who have access to client portals." },
    inbox:    { title:"Inbox",            subtitle:"Notifications and updates." },
    admin:    { title:"Admin",            subtitle:"Manage login access across all advisors, clients, and team members." },
    settings: { title:"Settings",         subtitle:"Account & workspace preferences." },
  };

  const action = nav.section === "clients" ? (
    <div style={{display:"flex",gap:8,alignItems:"center"}}>
      {!isMobile && (
        <div style={{display:"flex",border:`1px solid ${C.border}`,borderRadius:7,overflow:"hidden"}}>
          <button onClick={()=>setViewMode("list")} title="List view"
            style={{background:viewMode==="list"?C.navyFaint:C.surface,color:viewMode==="list"?C.navy:C.charMid,border:"none",padding:"8px 11px",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <List size={16}/>
          </button>
          <button onClick={()=>setViewMode("grid")} title="Grid view"
            style={{background:viewMode==="grid"?C.navyFaint:C.surface,color:viewMode==="grid"?C.navy:C.charMid,border:"none",borderLeft:`1px solid ${C.border}`,padding:"8px 11px",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <LayoutGrid size={16}/>
          </button>
        </div>
      )}
      <button onClick={()=>setShowAddClient(p=>!p)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
        {showAddClient?"Cancel":"+ Add New Client"}
      </button>
    </div>
  ) : nav.section === "advisors" ? (
    <button onClick={()=>setShowAddAdvisor(p=>!p)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
      {showAddAdvisor?"Cancel":"+ Add New Advisor"}
    </button>
  ) : null;

  return (
    <AppShell
      user={user}
      roleLabel="Master"
      onLogout={onLogout}
      sidebarItems={sidebarItems}
      currentSection={nav.section}
      onNavigate={nav.navigate}
      showAdmin={true}
      pageHeader={headerByTab[nav.section] ? { ...headerByTab[nav.section], action } : null}>

      {/* Add New Client form */}
      {showAddClient&&nav.section==="clients"&&(
        <AddClientForm advisorId={user.id} onCreated={()=>reloadClients&&reloadClients()}/>
      )}

      {/* Knowledge Base tab — owns its own header */}
      {nav.section==="knowledge"&&<KnowledgeBaseTab user={user} isMaster={true}/>}

      {/* Advisors tab */}
      {nav.section==="advisors"&&(
        <div>
          {/* Add New Advisor form */}
          {showAddAdvisor&&(
            <Card style={{padding:"22px 24px",marginBottom:20}}>
              <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,marginBottom:4}}>Add New Advisor</div>
              <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:16}}>Create an advisor profile and generate their unique invite link.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:14}}>
                {[["firstName","First Name","First Name"],["lastName","Last Name","Last Name"],["email","Email","Email Address"]].map(([k,l,ph])=>(
                  <div key={k}>
                    <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>{l}</label>
                    <input value={newAdvisor[k]} onChange={e=>setNewAdvisor(p=>({...p,[k]:e.target.value}))} placeholder={ph}
                      style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                ))}
                <div>
                  <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>5-Digit PIN</label>
                  <input value={newAdvisor.pin} onChange={e=>setNewAdvisor(p=>({...p,pin:e.target.value.replace(/\D/g,"").slice(0,5)}))} placeholder="12345" maxLength={5}
                    style={{width:"100%",background:C.bgAlt,border:`1px solid ${newAdvisor.pin.length===5?C.green:C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.mono,fontSize:16,letterSpacing:".2em",boxSizing:"border-box"}}/>
                </div>
              </div>
              <Btn variant="primary" onClick={handleAddAdvisor} loading={addingAdvisor} disabled={!newAdvisor.firstName||!newAdvisor.email||newAdvisor.pin.length!==5}>Create Advisor &amp; Generate Link</Btn>
              {newAdvisorInviteUrl&&(
                <div style={{marginTop:16,background:C.greenBg,border:`1px solid ${C.greenBd}`,borderRadius:8,padding:"14px 16px"}}>
                  <div style={{fontFamily:F.body,fontSize:12,color:C.green,fontWeight:700,marginBottom:6}}>Advisor created! Share this invite link:</div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <div style={{flex:1,fontFamily:F.mono,fontSize:11,color:C.charcoal,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",wordBreak:"break-all"}}>{newAdvisorInviteUrl}</div>
                    <button onClick={copyAdvisorUrl} style={{background:copiedAdvisor?C.green:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"8px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>
                      {copiedAdvisor?"Copied!":"Copy Link"}
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Advisors list */}
          {advisors.length===0?(
            <div style={{borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:"22px 18px",textAlign:"center"}}>
              <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No advisors yet</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Click "+ Add New Advisor" to invite your first advisor.</div>
            </div>
          ):(
            <div>
              {advisors.map((adv,i)=>{
                const isFirst=i===0;
                const isLast=i===advisors.length-1;
                const isClaimed=adv.invite_claimed;
                const displayName=adv.user?.name||adv.temp_name||"Advisor";
                const displayEmail=adv.user?.email||adv.temp_email||"—";
                const resendOpen=resendAdvisorId===adv.id;
                return (
                  <div key={adv.id}>
                    <div
                      onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                      onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                      style={{
                        padding:"22px 18px",
                        borderTop:isFirst?`1px solid ${C.border}`:"none",
                        borderBottom:(isLast||resendOpen)?"none":`1px solid ${C.border}`,
                        display:"flex",alignItems:"center",gap:14,
                        transition:"background .15s",
                      }}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:600}}>{displayName}</div>
                        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{displayEmail}</div>
                        <div style={{marginTop:6}}>
                          <Badge label={isClaimed?"Active":"Pending Invite"} type={isClaimed?"active":"pending"}/>
                        </div>
                      </div>
                      <div style={{display:"flex",marginLeft:"auto",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                        {isClaimed&&onViewAdvisor&&(
                          <ActionButton variant="primary" label="Enter Portal" onClick={()=>onViewAdvisor(adv)}/>
                        )}
                        {!isClaimed&&(
                          <>
                            <ActionButton variant="secondary" label={copiedRowToken===adv.invite_token?"Copied!":"Copy Link"} onClick={()=>copyRowUrl(adv.invite_token)}/>
                            <ActionButton variant="secondary" label="Resend Invite" onClick={()=>setResendAdvisorId(prev=>prev===adv.id?null:adv.id)}/>
                          </>
                        )}
                      </div>
                    </div>
                    {resendOpen&&(
                      <div style={{padding:"14px 18px 18px",background:C.greenBg,borderBottom:isLast?"none":`1px solid ${C.border}`}}>
                        <label style={{fontFamily:F.body,fontSize:9,color:C.green,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Copy-Paste Message</label>
                        <textarea readOnly value={buildAdvisorInviteMessage(adv)} rows={11}
                          style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box",resize:"vertical",marginBottom:10}}/>
                        <div style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between"}}>
                          <button onClick={()=>copyResendMessage(adv.id,buildAdvisorInviteMessage(adv))}
                            style={{background:copiedResendId===adv.id?C.green:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                            {copiedResendId===adv.id?"Copied!":"Copy Message"}
                          </button>
                          <button onClick={()=>setResendAdvisorId(null)}
                            style={{background:C.surface,color:C.navy,border:`1px solid ${C.navy}`,borderRadius:7,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                            Finished
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Clients tab */}
      {nav.section==="clients"&&(
        <div>
          {/* Recent activity alert — filterable */}
          {activeClientCount>0&&(
            <div onClick={()=>setFilterByActivity(p=>!p)}
              onMouseEnter={e=>{if(!filterByActivity)e.currentTarget.style.background=C.navyBorder;}}
              onMouseLeave={e=>{if(!filterByActivity)e.currentTarget.style.background=C.navyFaint;}}
              style={{display:"flex",alignItems:"center",gap:10,
                background:filterByActivity?C.navy:C.navyFaint,
                color:filterByActivity?"#fff":C.navy,
                border:`1px solid ${filterByActivity?C.navy:C.navyBorder}`,
                borderRadius:8,padding:"9px 14px",marginBottom:14,
                cursor:"pointer",transition:"background .15s",
                fontFamily:F.body,fontSize:12}}>
              <Activity size={14}/>
              <span style={{flex:1}}>Recent activity in the last 7 days ({activeClientCount} client{activeClientCount>1?"s":""})</span>
              {filterByActivity&&(
                <button onClick={e=>{e.stopPropagation();setFilterByActivity(false);}}
                  style={{background:"transparent",color:"inherit",border:"1px solid currentColor",borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  Clear filter
                </button>
              )}
            </div>
          )}

          {/* Stats — lines-not-boxes */}
          <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
            {[
              {l:"Total Clients",v:clients.length,c:C.navy},
              {l:"Assessments Done",v:assessmentsDone,c:C.green},
              {l:"Active Projects",v:totalProjects,c:C.teal},
              {l:"YTD Revenue",v:fmtMoney(totalRevenue),c:C.green},
              {l:"Outstanding",v:fmtMoney(outstanding),c:outstanding>0?C.amber:C.charLight},
            ].map(s=>(
              <div key={s.l} style={{flex:1,minWidth:130,padding:"14px 16px"}}>
                <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:5}}>{s.l}</div>
                <div style={{fontFamily:F.display,fontSize:28,color:s.c,fontWeight:700,lineHeight:1}}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* Tabs by advisor */}
          <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
            <div style={{display:"flex",border:`1px solid ${C.border}`,borderRadius:7,overflow:"hidden"}}>
              <button onClick={()=>setClientTab("master")}
                style={{background:clientTab==="master"?C.navyFaint:C.surface,color:clientTab==="master"?C.navy:C.charMid,border:"none",borderRight:`1px solid ${C.border}`,padding:"8px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                Master Clients {masterClients.length}
              </button>
              <button onClick={()=>setClientTab("advisor")}
                style={{background:clientTab==="advisor"?C.navyFaint:C.surface,color:clientTab==="advisor"?C.navy:C.charMid,border:"none",padding:"8px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                Advisor Clients {advisorClients.length}
              </button>
            </div>
            {clientTab==="advisor"&&advisorList.length>0&&(
              <select value={advisorFilter} onChange={e=>setAdvisorFilter(e.target.value)}
                style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 12px",fontFamily:F.body,fontSize:12,color:C.charcoal,cursor:"pointer"}}>
                <option value="all">Show all advisors</option>
                {advisorList.map(a=>(<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
            )}
          </div>

          {/* Client list / grid — mobile always uses cards (viewMode
              is irrelevant under the breakpoint); desktop keeps the
              existing list/grid toggle. */}
          {isMobile ? (
            <div>
              {tabFilteredClients.map(cl => (
                <ClientCard key={cl.id} cl={cl} activity={activity} showAdvisorPill={clientTab==="advisor"} onViewClient={onViewClient}/>
              ))}
            </div>
          ) : viewMode==="list"?(
            <div>
              {tabFilteredClients.map((cl,i)=>{
                const isFirst=i===0;
                const isLast=i===tabFilteredClients.length-1;
                const count=activity[cl.id]?.count||0;
                const latest=activity[cl.id]?.latest;
                const projectCount=(cl.projects||[]).length;
                const revenue=(cl.invoices||[]).filter(inv=>inv.status==="paid").reduce((a,inv)=>a+Number(inv.amount),0);
                return (
                  <div key={cl.id}
                    onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                    onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                    style={{
                      padding:"22px 18px",
                      borderTop:isFirst?`1px solid ${C.border}`:"none",
                      borderBottom:isLast?"none":`1px solid ${C.border}`,
                      display:"flex",alignItems:"center",gap:14,
                      transition:"background .15s",
                    }}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600}}>{cl.user?.name||cl.temp_name||"Client"}</div>
                        {count>0&&(
                          <div title={`${count} update${count>1?"s":""} in last 7 days`}
                            style={{background:C.red,color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:8,fontWeight:700,flexShrink:0}}>
                            {count}
                          </div>
                        )}
                        <Badge label={cl.assessment_complete?"Assessment done":"Assessment pending"} type={cl.assessment_complete?"complete":"pending"}/>
                        {clientTab==="advisor"&&(
                          <span style={{display:"inline-flex",alignItems:"center",gap:4,background:C.purpleBg,color:C.purple,border:`1px solid ${C.purpleBd}`,borderRadius:10,padding:"2px 8px",fontFamily:F.body,fontSize:10,fontWeight:600}}>
                            <span>⊙</span><span>{cl.advisor?.name||cl.advisor?.email||"Unknown advisor"}</span>
                          </span>
                        )}
                      </div>
                      <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",fontFamily:F.body,fontSize:12,color:C.charMid}}>
                        <span>{cl.company}</span>
                        {latest&&<span style={{fontFamily:F.mono,fontSize:10,color:C.navyMid}}>Latest: {fmtDate(latest)}</span>}
                        <span><span style={{color:C.charLight}}>Projects </span><span style={{color:C.charcoal,fontWeight:600}}>{projectCount}</span></span>
                        <span><span style={{color:C.charLight}}>Revenue </span><span style={{color:C.green,fontWeight:600}}>{fmtMoney(revenue)}</span></span>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,flexShrink:0}}>
                      <ActionButton variant="primary" label="Enter Portal" onClick={()=>onViewClient(cl,false)}/>
                      <ActionButton variant="warning" label="Preview" onClick={()=>onViewClient(cl,true)}/>
                      <ActionButton variant="secondary" label="Manage" onClick={()=>onViewClient(cl,false,"admin")}/>
                    </div>
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {tabFilteredClients.map(cl=>{
                const count=activity[cl.id]?.count||0;
                const latest=activity[cl.id]?.latest;
                return (
                  <div key={cl.id} style={{background:C.bgAlt,border:"1px solid transparent",borderRadius:12,padding:"20px 22px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:1,flexWrap:"wrap"}}>
                          <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,fontWeight:600}}>{cl.user?.name||cl.temp_name||"Client"}</div>
                          {count>0&&(
                            <div title={`${count} update${count>1?"s":""} in last 7 days`}
                              style={{background:C.red,color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:8,fontWeight:700,flexShrink:0}}>
                              {count}
                            </div>
                          )}
                          {clientTab==="advisor"&&(
                            <span style={{display:"inline-flex",alignItems:"center",gap:4,background:C.purpleBg,color:C.purple,border:`1px solid ${C.purpleBd}`,borderRadius:10,padding:"2px 8px",fontFamily:F.body,fontSize:10,fontWeight:600}}>
                              <span>⊙</span><span>{cl.advisor?.name||cl.advisor?.email||"Unknown advisor"}</span>
                            </span>
                          )}
                        </div>
                        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{cl.company}</div>
                        {latest&&<div style={{fontFamily:F.mono,fontSize:9,color:C.navyMid,marginTop:2}}>Latest: {fmtDate(latest)}</div>}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                        <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Assessment</div>
                        <Badge label={cl.assessment_complete?"Done":"Pending"} type={cl.assessment_complete?"complete":"pending"}/>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:14}}>
                      <div><div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginBottom:1}}>Projects</div><div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>{(cl.projects||[]).length}</div></div>
                      <div><div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginBottom:1}}>Revenue</div><div style={{fontFamily:F.body,fontSize:13,color:C.green,fontWeight:600}}>{fmtMoney((cl.invoices||[]).filter(inv=>inv.status==="paid").reduce((a,inv)=>a+Number(inv.amount),0))}</div></div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <ActionButton variant="primary" label="Enter Portal" onClick={()=>onViewClient(cl,false)} block/>
                      <ActionButton variant="warning" label="Preview" onClick={()=>onViewClient(cl,true)} block/>
                      <ActionButton variant="secondary" label="Manage" onClick={()=>onViewClient(cl,false,"admin")} block/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Inbox / Settings — placeholders until later steps */}
      {nav.section==="inbox"&&(
        <Card style={{padding:40,textAlign:"center",fontFamily:F.body,color:C.charLight}}>Inbox is coming online in a later step.</Card>
      )}
      {nav.section==="admin"&&(
        <AdminDirectory user={user}/>
      )}
      {nav.section==="settings"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <ChangeEmailCard isImpersonating={isImpersonating} />
          <ChangePasswordCard isImpersonating={isImpersonating} />
        </div>
      )}

      <div style={{textAlign:"center",padding:"24px 0 12px",fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
    </AppShell>
  );
}

// ── Advisor view ──────────────────────────────────────────────
function AdvisorView({user, clients, isImpersonating=false, onViewClient, onLogout, reloadClients, banner}) {
  const nav = useNavState("clients");
  const isMobile = useIsMobile();
  const [showAddClient,setShowAddClient]=useState(false);
  const [viewMode,setViewMode]=useState("list");
  const [filterByActivity,setFilterByActivity]=useState(false);
  const [activity,setActivity]=useState({});

  const sidebarItems = [
    { id:"clients",   icon:Users,     label:"My Clients" },
    { id:"knowledge", icon:BookOpen,  label:"Knowledge Base" },
    { id:"usage",     icon:BarChart3, label:"My Usage" },
  ];

  // knowledge intentionally omitted — KnowledgeBaseTab renders its own header.
  const headerByTab = {
    clients:  { title:"Advisor Dashboard", subtitle:"Your clients and engagement" },
    usage:    { title:"Usage",             subtitle:"Your activity and engagement insights" },
    inbox:    { title:"Inbox",             subtitle:"Notifications and updates." },
    admin:    { title:"Admin",             subtitle:"Manage client & team member accounts" },
    settings: { title:"Settings",          subtitle:"Account & workspace preferences." },
  };

  // Per-client recent activity (last 7d). Mirrors MasterView's loader; the
  // advisor's clients prop is already scoped to their roster, so no extra
  // filtering by advisor_id is needed.
  useEffect(()=>{
    const ids=clients.map(c=>c.id).filter(Boolean);
    if(ids.length>0){
      getRecentActivityForClients(ids).then(setActivity).catch(()=>{});
    }
  },[clients]);

  const totalProjects=clients.reduce((a,c)=>a+(c.projects?.length||0),0);
  const assessmentsDone=clients.filter(c=>c.assessment_complete).length;
  const totalRevenue=clients.reduce((a,c)=>a+(c.invoices||[]).filter(i=>i.status==="paid").reduce((s,i)=>s+Number(i.amount),0),0);
  const outstanding=clients.reduce((a,c)=>a+(c.invoices||[]).filter(i=>i.status!=="paid").reduce((s,i)=>s+Number(i.amount),0),0);
  const activeClientCount=clients.filter(c=>activity[c.id]?.count>0).length;
  const visibleClients=filterByActivity?clients.filter(c=>activity[c.id]?.count>0):clients;

  const action = nav.section === "clients" ? (
    <div style={{display:"flex",gap:8,alignItems:"center"}}>
      {!isMobile && (
        <div style={{display:"flex",border:`1px solid ${C.border}`,borderRadius:7,overflow:"hidden"}}>
          <button onClick={()=>setViewMode("list")} title="List view"
            style={{background:viewMode==="list"?C.navyFaint:C.surface,color:viewMode==="list"?C.navy:C.charMid,border:"none",padding:"8px 11px",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <List size={16}/>
          </button>
          <button onClick={()=>setViewMode("grid")} title="Grid view"
            style={{background:viewMode==="grid"?C.navyFaint:C.surface,color:viewMode==="grid"?C.navy:C.charMid,border:"none",borderLeft:`1px solid ${C.border}`,padding:"8px 11px",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <LayoutGrid size={16}/>
          </button>
        </div>
      )}
      <button onClick={()=>setShowAddClient(p=>!p)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
        {showAddClient?"Cancel":"+ Add New Client"}
      </button>
    </div>
  ) : null;

  return (
    <AppShell
      user={user}
      roleLabel="Advisor"
      onLogout={onLogout}
      sidebarItems={sidebarItems}
      currentSection={nav.section}
      onNavigate={nav.navigate}
      showAdmin={true}
      banner={banner}
      pageHeader={headerByTab[nav.section] ? { ...headerByTab[nav.section], action } : null}>

      {/* Add New Client form */}
      {showAddClient&&nav.section==="clients"&&(
        <AddClientForm advisorId={user.id} onCreated={()=>reloadClients&&reloadClients()}/>
      )}

      {/* My Clients */}
      {nav.section==="clients"&&(
        <div>
          {/* Recent activity alert — filterable */}
          {activeClientCount>0&&(
            <div onClick={()=>setFilterByActivity(p=>!p)}
              onMouseEnter={e=>{if(!filterByActivity)e.currentTarget.style.background=C.navyBorder;}}
              onMouseLeave={e=>{if(!filterByActivity)e.currentTarget.style.background=C.navyFaint;}}
              style={{display:"flex",alignItems:"center",gap:10,
                background:filterByActivity?C.navy:C.navyFaint,
                color:filterByActivity?"#fff":C.navy,
                border:`1px solid ${filterByActivity?C.navy:C.navyBorder}`,
                borderRadius:8,padding:"9px 14px",marginBottom:14,
                cursor:"pointer",transition:"background .15s",
                fontFamily:F.body,fontSize:12}}>
              <Activity size={14}/>
              <span style={{flex:1}}>Recent activity in the last 7 days ({activeClientCount} client{activeClientCount>1?"s":""})</span>
              {filterByActivity&&(
                <button onClick={e=>{e.stopPropagation();setFilterByActivity(false);}}
                  style={{background:"transparent",color:"inherit",border:"1px solid currentColor",borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  Clear filter
                </button>
              )}
            </div>
          )}

          {/* Stats — lines-not-boxes */}
          <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
            {[
              {l:"Total Clients",v:clients.length,c:C.navy},
              {l:"Assessments Done",v:assessmentsDone,c:C.green},
              {l:"Active Projects",v:totalProjects,c:C.teal},
              {l:"YTD Revenue",v:fmtMoney(totalRevenue),c:C.green},
              {l:"Outstanding",v:fmtMoney(outstanding),c:outstanding>0?C.amber:C.charLight},
            ].map(s=>(
              <div key={s.l} style={{flex:1,minWidth:130,padding:"14px 16px"}}>
                <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:5}}>{s.l}</div>
                <div style={{fontFamily:F.display,fontSize:28,color:s.c,fontWeight:700,lineHeight:1}}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* Client list / grid — mobile always uses cards (viewMode
              is irrelevant under the breakpoint); desktop keeps the
              existing list/grid toggle. */}
          {isMobile ? (
            <div>
              {visibleClients.map(cl => (
                <ClientCard key={cl.id} cl={cl} activity={activity} onViewClient={onViewClient}/>
              ))}
            </div>
          ) : viewMode==="list"?(
            <div>
              {visibleClients.map((cl,i)=>{
                const isFirst=i===0;
                const isLast=i===visibleClients.length-1;
                const count=activity[cl.id]?.count||0;
                const latest=activity[cl.id]?.latest;
                const projectCount=(cl.projects||[]).length;
                const revenue=(cl.invoices||[]).filter(inv=>inv.status==="paid").reduce((a,inv)=>a+Number(inv.amount),0);
                return (
                  <div key={cl.id}
                    onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                    onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                    style={{
                      padding:"22px 18px",
                      borderTop:isFirst?`1px solid ${C.border}`:"none",
                      borderBottom:isLast?"none":`1px solid ${C.border}`,
                      display:"flex",alignItems:"center",gap:14,
                      transition:"background .15s",
                    }}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600}}>{cl.user?.name||cl.temp_name||"Client"}</div>
                        {count>0&&(
                          <div title={`${count} update${count>1?"s":""} in last 7 days`}
                            style={{background:C.red,color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:8,fontWeight:700,flexShrink:0}}>
                            {count}
                          </div>
                        )}
                        <Badge label={cl.assessment_complete?"Assessment done":"Assessment pending"} type={cl.assessment_complete?"complete":"pending"}/>
                      </div>
                      <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",fontFamily:F.body,fontSize:12,color:C.charMid}}>
                        <span>{cl.company}</span>
                        {latest&&<span style={{fontFamily:F.mono,fontSize:10,color:C.navyMid}}>Latest: {fmtDate(latest)}</span>}
                        <span><span style={{color:C.charLight}}>Projects </span><span style={{color:C.charcoal,fontWeight:600}}>{projectCount}</span></span>
                        <span><span style={{color:C.charLight}}>Revenue </span><span style={{color:C.green,fontWeight:600}}>{fmtMoney(revenue)}</span></span>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,flexShrink:0}}>
                      <ActionButton variant="primary" label="Enter Portal" onClick={()=>onViewClient(cl,false)}/>
                      <ActionButton variant="warning" label="Preview" onClick={()=>onViewClient(cl,true)}/>
                      <ActionButton variant="secondary" label="Manage" onClick={()=>onViewClient(cl,false,"admin")}/>
                    </div>
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {visibleClients.map(cl=>{
                const count=activity[cl.id]?.count||0;
                const latest=activity[cl.id]?.latest;
                return (
                  <div key={cl.id} style={{background:C.bgAlt,border:"1px solid transparent",borderRadius:12,padding:"20px 22px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:1,flexWrap:"wrap"}}>
                          <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,fontWeight:600}}>{cl.user?.name||cl.temp_name||"Client"}</div>
                          {count>0&&(
                            <div title={`${count} update${count>1?"s":""} in last 7 days`}
                              style={{background:C.red,color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:8,fontWeight:700,flexShrink:0}}>
                              {count}
                            </div>
                          )}
                        </div>
                        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{cl.company}</div>
                        {latest&&<div style={{fontFamily:F.mono,fontSize:9,color:C.navyMid,marginTop:2}}>Latest: {fmtDate(latest)}</div>}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                        <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Assessment</div>
                        <Badge label={cl.assessment_complete?"Done":"Pending"} type={cl.assessment_complete?"complete":"pending"}/>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:14}}>
                      <div><div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginBottom:1}}>Projects</div><div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>{(cl.projects||[]).length}</div></div>
                      <div><div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginBottom:1}}>Revenue</div><div style={{fontFamily:F.body,fontSize:13,color:C.green,fontWeight:600}}>{fmtMoney((cl.invoices||[]).filter(inv=>inv.status==="paid").reduce((a,inv)=>a+Number(inv.amount),0))}</div></div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <ActionButton variant="primary" label="Enter Portal" onClick={()=>onViewClient(cl,false)} block/>
                      <ActionButton variant="warning" label="Preview" onClick={()=>onViewClient(cl,true)} block/>
                      <ActionButton variant="secondary" label="Manage" onClick={()=>onViewClient(cl,false,"admin")} block/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Knowledge Base — owns its own header */}
      {nav.section==="knowledge"&&<KnowledgeBaseTab user={user} isMaster={false}/>}

      {/* My Usage */}
      {nav.section==="usage"&&<UsageDashboardView userId={user.id}/>}

      {/* Inbox / Settings — placeholders until later steps */}
      {nav.section==="inbox"&&(
        <Card style={{padding:40,textAlign:"center",fontFamily:F.body,color:C.charLight}}>Inbox is coming online in a later step.</Card>
      )}
      {nav.section==="admin"&&(
        <AdminDirectory user={user}/>
      )}
      {nav.section==="settings"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <ChangeEmailCard isImpersonating={isImpersonating} />
          <ChangePasswordCard isImpersonating={isImpersonating} />
        </div>
      )}

      <div style={{textAlign:"center",padding:"24px 0 12px",fontFamily:F.body,fontSize:10,color:C.charLight}}>a BB Integrated product</div>
    </AppShell>
  );
}

// ── KNOWLEDGE BASE TAB ─────────────────────────────────────────
function KnowledgeBaseTab({user, isMaster}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterScope, setFilterScope] = useState("all");
  const [editingEntry, setEditingEntry] = useState(null); // null | "new" | entry object
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadEntries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadEntries() {
    setLoading(true);
    try {
      const data = await getKnowledgeBase(user.id, isMaster);
      setEntries(data);
    } catch(e) {
      console.error("Failed to load knowledge base:", e);
    }
    setLoading(false);
  }

  async function handleSave(entryData) {
    try {
      if (editingEntry === "new") {
        await createKnowledgeEntry({
          ...entryData,
          advisorId: entryData.scope === "brian_specific" ? user.id : null,
        });
      } else {
        await updateKnowledgeEntry(editingEntry.id, {
          category: entryData.category,
          title: entryData.title,
          content: entryData.content,
          tags: entryData.tags,
          advisor_id: entryData.scope === "brian_specific" ? user.id : null,
        });
      }
      setEditingEntry(null);
      await loadEntries();
    } catch(e) {
      alert("Save failed: " + e.message);
    }
  }

  async function handleDelete(entryId) {
    try {
      await deleteKnowledgeEntry(entryId);
      setDeleteConfirm(null);
      await loadEntries();
    } catch(e) {
      alert("Delete failed: " + e.message);
    }
  }

  // Get unique categories from current entries
  const allCategories = [...new Set(entries.map(e => e.category))].sort();
  
  // Filter entries
  const filtered = entries.filter(e => {
    if (filterCategory !== "all" && e.category !== filterCategory) return false;
    if (filterScope === "universal" && e.advisor_id !== null) return false;
    if (filterScope === "advisor" && e.advisor_id === null) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.title.toLowerCase().includes(q) ||
             e.content.toLowerCase().includes(q) ||
             (e.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  // Group by category
  const grouped = filtered.reduce((acc, e) => {
    if (!acc[e.category]) acc[e.category] = [];
    acc[e.category].push(e);
    return acc;
  }, {});

  const categoryLabel = (cat) => cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const canEdit = (entry) => isMaster || entry.advisor_id === user.id;

  return (
    <div>
      {editingEntry && (
        <KnowledgeEntryModal
          entry={editingEntry === "new" ? null : editingEntry}
          allCategories={allCategories}
          isMaster={isMaster}
          canEdit={editingEntry === "new" ? true : canEdit(editingEntry)}
          onSave={handleSave}
          onClose={() => setEditingEntry(null)}
        />
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,margin:0}}>Knowledge Base</h2>
          <p style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:5}}>
            Coaching wisdom, frameworks, and stories the AI uses to power every suggestion across the portal.
          </p>
        </div>
        <button onClick={() => setEditingEntry("new")} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
          + New Entry
        </button>
      </div>

      {/* Search and filters — lines-not-boxes: no surrounding card */}
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search title, content, or tags..."
            style={{flex:"1 1 240px",minWidth:200,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}
          />
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,minWidth:160}}
          >
            <option value="all">All Categories</option>
            {allCategories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
          <select
            value={filterScope}
            onChange={e => setFilterScope(e.target.value)}
            style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,minWidth:140}}
          >
            <option value="all">All Scopes</option>
            <option value="universal">Universal Only</option>
            <option value="advisor">Advisor-Specific</option>
          </select>
        </div>
        <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,marginTop:8}}>
          {filtered.length} of {entries.length} entries
        </div>
      </div>

      {loading && (
        <Card style={{padding:40,textAlign:"center"}}>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Loading...</div>
        </Card>
      )}

      {!loading && filtered.length === 0 && (
        <Card style={{padding:40,textAlign:"center"}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No entries found</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Try adjusting your search or filters.</div>
        </Card>
      )}

      {!loading && Object.keys(grouped).sort().map(category => {
        const rows = grouped[category];
        return (
          <div key={category} style={{marginBottom:28}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <h3 style={{fontFamily:F.display,fontSize:17,color:C.navy,fontWeight:600,margin:0}}>{categoryLabel(category)}</h3>
              <span style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>{rows.length} {rows.length === 1 ? 'entry' : 'entries'}</span>
            </div>
            <div>
              {rows.map((entry, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === rows.length - 1;
                const tags = entry.tags || [];
                const visibleTags = tags.slice(0, 4);
                const overflow = tags.length - 4;
                return (
                  <div key={entry.id}
                    onMouseEnter={e => { if (deleteConfirm !== entry.id) e.currentTarget.style.background = C.bgAlt; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    style={{
                      padding:"16px 18px",
                      borderTop: isFirst ? `1px solid ${C.border}` : "none",
                      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
                      transition:"background .15s",
                    }}>
                    {deleteConfirm === entry.id ? (
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600}}>Delete "{entry.title}"?</span>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={() => handleDelete(entry.id)} style={{background:C.red,color:"#fff",border:"none",borderRadius:6,padding:"5px 14px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
                          <button onClick={() => setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:6}}>
                          <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={() => setEditingEntry(entry)}>
                            <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:600,marginBottom:4}}>{entry.title}</div>
                            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              <Badge label={entry.advisor_id ? "Brian-Specific" : "Universal"} type={entry.advisor_id ? "brian_specific" : "universal"}/>
                              {tags.length > 0 && (
                                <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>
                                  {visibleTags.join(" · ")}
                                  {overflow > 0 && ` · +${overflow} more`}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{display:"flex",gap:6,flexShrink:0,alignItems:"center"}}>
                            {canEdit(entry) ? (
                              <>
                                <button onClick={() => setEditingEntry(entry)} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Edit</button>
                                <button onClick={() => setDeleteConfirm(entry.id)} style={{background:C.bgAlt,color:C.charLight,border:`1px solid ${C.border}`,borderRadius:6,width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:13}}>🗑</button>
                              </>
                            ) : (
                              <span title="Master's entry — read-only for advisors" style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",padding:"4px 9px",background:C.bgAlt,borderRadius:5,border:`1px solid ${C.border}`}}>Read-only</span>
                            )}
                          </div>
                        </div>
                        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.6,marginTop:6,whiteSpace:"pre-wrap",overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>
                          {entry.content}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── KNOWLEDGE ENTRY MODAL ──────────────────────────────────────
function KnowledgeEntryModal({entry, allCategories, isMaster, canEdit = true, onSave, onClose}) {
  const [title, setTitle] = useState(entry?.title || "");
  const [category, setCategory] = useState(entry?.category || allCategories[0] || "philosophy");
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [content, setContent] = useState(entry?.content || "");
  const [tagsInput, setTagsInput] = useState((entry?.tags || []).join(", "));
  const [scope, setScope] = useState(entry?.advisor_id ? "brian_specific" : "universal");
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!title.trim() || !content.trim()) {
      alert("Title and content are required.");
      return;
    }
    setSaving(true);
    const finalCategory = showCustomCategory ? customCategory.trim().toLowerCase().replace(/\s+/g, '_') : category;
    if (showCustomCategory && !finalCategory) {
      alert("Please enter a category name.");
      setSaving(false);
      return;
    }
    const tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
    await onSave({
      title: title.trim(),
      category: finalCategory,
      content: content.trim(),
      tags,
      scope,
    });
    setSaving(false);
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.surface,borderRadius:14,width:"100%",maxWidth:720,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.25)"}}>
        {/* Header */}
        <div style={{padding:"20px 24px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:F.display,fontSize:20,color:C.charcoal,fontWeight:600}}>
            {entry ? (canEdit ? "Edit Knowledge Entry" : "View Knowledge Entry") : "New Knowledge Entry"}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,color:C.charLight,cursor:"pointer",padding:4}}>×</button>
        </div>

        {/* Body */}
        <div style={{padding:"20px 24px",overflowY:"auto",flex:1}}>
          {/* Title */}
          <div style={{marginBottom:16}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Short, descriptive title" readOnly={!canEdit}
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"10px 13px",color:C.charcoal,fontFamily:F.body,fontSize:14,boxSizing:"border-box"}}/>
          </div>

          {/* Category + Scope */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Category</label>
              {showCustomCategory ? (
                <div style={{display:"flex",gap:6}}>
                  <input value={customCategory} onChange={e => setCustomCategory(e.target.value)} placeholder="new_category_name" autoFocus
                    style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.mono,fontSize:13,boxSizing:"border-box"}}/>
                  <button onClick={() => setShowCustomCategory(false)} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>↺</button>
                </div>
              ) : (
                <div style={{display:"flex",gap:6}}>
                  <select value={category} onChange={e => setCategory(e.target.value)} disabled={!canEdit}
                    style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13}}>
                    {allCategories.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                  </select>
                  {canEdit && <button onClick={() => setShowCustomCategory(true)} title="Add new category"
                    style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"6px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>+ New</button>}
                </div>
              )}
            </div>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Scope</label>
              <select value={scope} onChange={e => setScope(e.target.value)} disabled={!canEdit}
                style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13}}>
                <option value="universal">Universal — All advisors</option>
                <option value="brian_specific">Advisor-Specific — Only my clients</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div style={{marginBottom:16}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Content</label>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={12} readOnly={!canEdit}
              placeholder="The full content of this entry. Write in your own voice — this is what the AI will reference."
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"10px 13px",color:C.charcoal,fontFamily:F.body,fontSize:13,lineHeight:1.7,resize:"vertical",boxSizing:"border-box",minHeight:200}}/>
            <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:4}}>{content.length} characters</div>
          </div>

          {/* Tags */}
          <div style={{marginBottom:8}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Tags</label>
            <input value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="leadership, character, story (comma-separated)" readOnly={!canEdit}
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:4}}>Tags help the AI find relevant entries faster. Use lowercase, comma-separated.</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{padding:"14px 24px",borderTop:`1px solid ${C.border}`,display:"flex",gap:10,justifyContent:"flex-end"}}>
          {canEdit ? (
            <>
              <button onClick={onClose} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,cursor:"pointer"}}>Cancel</button>
              <button onClick={handleSubmit} disabled={saving || !title.trim() || !content.trim()}
                style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 22px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:saving || !title.trim() || !content.trim() ? "default" : "pointer",opacity:saving || !title.trim() || !content.trim() ? .55 : 1}}>
                {saving ? "Saving…" : entry ? "Save Changes" : "Create Entry"}
              </button>
            </>
          ) : (
            <button onClick={onClose} style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 22px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Usage Dashboard (simple placeholder — full version in portal) ──
function UsageDashboardView({userId}) {
  const LIMITS = {ai_messages:{label:"AI Message Reviews",free:5},ai_files:{label:"File AI Interpretations",free:3},ai_sessions:{label:"Session Transcripts",free:2},ai_gap_summaries:{label:"Gap Analysis Summaries",free:3}};
  const [usage, setUsage] = useState(null);

  useEffect(()=>{
    import('./supabase').then(({getUsageRecord})=>getUsageRecord(userId).then(setUsage));
  },[userId]);

  return (
    <div>
      <SectionHead title="My Usage" sub="Monthly AI feature usage — resets on the 1st"/>
      {Object.entries(LIMITS).map(([key,lim])=>{
        const used = usage?.[key]||0;
        const rem = Math.max(0,lim.free-used);
        const color = rem===0?C.red:rem===1?C.amber:C.green;
        return (
          <Card key={key} style={{padding:"14px 18px",marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{lim.label}</span>
              <span style={{fontFamily:F.mono,fontSize:11,color,fontWeight:600}}>{rem}/{lim.free} remaining</span>
            </div>
            <div style={{background:C.bgAlt,borderRadius:3,height:5,overflow:"hidden"}}>
              <div style={{width:`${Math.min((used/lim.free)*100,100)}%`,height:"100%",background:color,borderRadius:3}}/>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── Client portal content — full Supabase-connected portal ───
function ClientPortalContent({client, isAdvisor, user, isImpersonating=false, reloadClients, advisorId, operatorRole=null, onUpgradeRequest, onBack, onLogout, banner, pageAccess, initialChromeSection}) {
  return (
    <Portal
      client={client}
      isAdvisor={isAdvisor}
      user={user}
      isImpersonating={isImpersonating}
      onLogout={onLogout||(() => window.location.reload())}
      onBack={onBack}
      advisorId={advisorId}
      operatorRole={operatorRole}
      onUpgradeRequest={onUpgradeRequest}
      banner={banner}
      pageAccess={pageAccess}
      initialChromeSection={initialChromeSection}
    />
  );
}
